/**
 * Image resize utility for AI Chat
 * Resizes images using Canvas API before uploading to S3
 * to optimize performance and reduce API payload size.
 */

const DEFAULT_MAX_DIMENSION = 2048
const DEFAULT_QUALITY = 0.85

/**
 * Resize an image file to a maximum dimension while preserving aspect ratio.
 * Converts to JPEG for smaller file size (unless PNG transparency is needed).
 *
 * @param file - Original image File
 * @param maxDimension - Maximum width or height (default: 2048px)
 * @param quality - JPEG quality 0-1 (default: 0.85)
 * @returns Promise<File> - Resized image as a new File object
 */
export async function resizeImageFile(
  file: File,
  maxDimension: number = DEFAULT_MAX_DIMENSION,
  quality: number = DEFAULT_QUALITY
): Promise<File> {
  return new Promise((resolve, reject) => {
    // Skip non-image files
    if (!file.type.startsWith('image/')) {
      resolve(file)
      return
    }

    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)

      const { width, height } = img

      // Skip if image is already smaller than maxDimension
      if (width <= maxDimension && height <= maxDimension) {
        // Still re-encode to compress if the file is large (> 1MB)
        if (file.size <= 1024 * 1024) {
          resolve(file)
          return
        }
      }

      // Calculate new dimensions maintaining aspect ratio
      let newWidth = width
      let newHeight = height

      if (width > height) {
        if (width > maxDimension) {
          newHeight = Math.round((height * maxDimension) / width)
          newWidth = maxDimension
        }
      } else {
        if (height > maxDimension) {
          newWidth = Math.round((width * maxDimension) / height)
          newHeight = maxDimension
        }
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas')
      canvas.width = newWidth
      canvas.height = newHeight

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      // Use high-quality image rendering
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, 0, 0, newWidth, newHeight)

      // Determine output format
      // Keep PNG for transparency, convert everything else to JPEG for smaller size
      const isPng = file.type === 'image/png'
      const outputType = isPng ? 'image/png' : 'image/jpeg'
      const outputQuality = isPng ? undefined : quality

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create image blob'))
            return
          }

          // Generate filename with correct extension
          const ext = isPng ? 'png' : 'jpg'
          const baseName = file.name.replace(/\.[^.]+$/, '') || 'image'
          const newFilename = `${baseName}.${ext}`

          const resizedFile = new File([blob], newFilename, {
            type: outputType,
            lastModified: Date.now()
          })

          console.log(
            `[ImageResize] ${width}x${height} (${formatBytes(file.size)}) → ${newWidth}x${newHeight} (${formatBytes(resizedFile.size)})`
          )

          resolve(resizedFile)
        },
        outputType,
        outputQuality
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Failed to load image for resizing'))
    }

    img.src = objectUrl
  })
}

/**
 * Create a small preview data URL from a File (for UI thumbnail)
 * Uses much smaller dimensions for preview display only.
 */
export async function createPreviewDataUrl(
  file: File,
  maxDimension: number = 200
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)

      let { width, height } = img

      if (width > height) {
        if (width > maxDimension) {
          height = Math.round((height * maxDimension) / width)
          width = maxDimension
        }
      } else {
        if (height > maxDimension) {
          width = Math.round((width * maxDimension) / height)
          height = maxDimension
        }
      }

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      ctx.drawImage(img, 0, 0, width, height)
      resolve(canvas.toDataURL('image/jpeg', 0.6))
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Failed to load image for preview'))
    }

    img.src = objectUrl
  })
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}
